import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading } from '../components/homepage';
import { whitePapers } from '../content/whitePapers';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPillSx,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const [featuredPaper, ...remainingPapers] = whitePapers;

const WhitePapersPage: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/white-papers" />
      </Helmet>
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5.5, md: 7.5 }, pb: { xs: 6, md: 7 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3.5, lg: 5 }} alignItems="center">
            <Grid item xs={12} lg={5}>
              <SectionHeading
                eyebrow="White papers"
                title="Your stack executes. Something has to decide. These papers explain the gap."
                description="Each paper approaches the problem from a different angle — what payment optimization actually requires, why stack replacements miss it, and what changes when a decision layer operates above what you already have."
                marginBottom={2.5}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Request Demo
                </Button>
                <Button component={RouterLink} to="/resources" sx={marketingSecondaryButtonSx}>
                  Back to resources
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Card sx={{ ...marketingCardSx, borderTop: '3px solid rgba(16,38,77,0.88)' }}>
                <Box component="span" sx={marketingPillSx}>
                  Featured
                </Box>
                <Typography sx={{ mt: 1.1, color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.3rem', md: '1.55rem' }, lineHeight: 1.22, mb: 0.8 }}>
                  {featuredPaper.title}
                </Typography>
                <Typography sx={{ color: marketingPalette.body, lineHeight: 1.72, mb: 1.35 }}>
                  {featuredPaper.cardDescription}
                </Typography>
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  {featuredPaper.highlights.slice(0, 3).map((item) => (
                    <Grid item xs={12} sm={4} key={item.label}>
                      <Box sx={{ px: 0.9, py: 0.85, border: '1px solid rgba(219,228,239,0.9)', backgroundColor: '#fff', height: '100%' }}>
                        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.16 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.88rem', lineHeight: 1.34 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button component={RouterLink} to={`/white-papers/${featuredPaper.slug}`} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Read featured paper
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="All papers"
            title="Use the papers to test the category from different angles."
            description="Some explain the category itself. Others explain why replacement projects are often the wrong first answer."
            marginBottom={3.5}
          />
          <Grid container spacing={2.5} alignItems="stretch">
            {remainingPapers.map((item) => (
              <Grid item xs={12} md={6} key={item.slug}>
                <Card sx={{ ...marketingCardSx, height: '100%', display: 'grid', alignContent: 'start' }}>
                  <Box component="span" sx={marketingPillSx}>
                    {item.label}
                  </Box>
                  <Typography sx={{ mt: 1.05, color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.18rem', md: '1.4rem' }, lineHeight: 1.24, mb: 0.75 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.72, mb: 1.25 }}>
                    {item.summary}
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75, mb: 1.35 }}>
                    {item.meta.slice(0, 2).map((meta) => (
                      <Box key={meta.label} sx={{ pb: 0.75, borderBottom: '1px solid rgba(219,228,239,0.84)' }}>
                        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.14 }}>
                          {meta.label}
                        </Typography>
                        <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, lineHeight: 1.48 }}>
                          {meta.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button component={RouterLink} to={`/white-papers/${item.slug}`} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                    Read white paper
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <MarketingFinalCta
        kicker="White papers · Next step"
        heading="Which of these reflects where your stack is today?"
        body="If one of these reads like your situation, that's the call to open. We'll take the argument off the page and into a look at your own data."
        secondary={{ label: 'Review case studies', to: '/case-studies' }}
      />

      <Footer />
    </Box>
  );
};

export default WhitePapersPage;
